import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { ClientMessage, ServerMessage } from "../api/types";

export type WsStatus = "idle" | "connecting" | "open" | "closed";

// Single WebSocket lifted out of the chat panel so the wizards can also send
// programmatic prompts (Step 1/2/3 of BAFA, etc.) and listen for `file_changed`
// / `import_prefill_result` frames without owning their own connection.

interface BridgeValue {
  status: WsStatus;
  send: (msg: ClientMessage) => boolean;
  /** Subscribe to every server frame; returns an unsubscribe function. */
  subscribe: (handler: (msg: ServerMessage) => void) => () => void;
  /** Latest list of models advertised by the server (`models` frame). */
  models: string[];
  /** Currently selected model id. Empty string = let the server pick the default. */
  selectedModel: string;
  setSelectedModel: (model: string) => void;
}

const BridgeContext = createContext<BridgeValue | null>(null);

const MODEL_STORAGE_KEY = "zww-agent.selectedModel";

function loadInitialSelectedModel(): string {
  try {
    return window.localStorage.getItem(MODEL_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function resolveUrl(): string {
  const override = (import.meta.env.VITE_WS_URL as string | undefined) ?? "";
  if (override) return override;
  const backend = (import.meta.env.VITE_BACKEND_URL as string | undefined) ?? "";
  if (backend) return backend.replace(/^http/, "ws").replace(/\/$/, "");
  const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${window.location.host}`;
}

interface ProviderProps {
  enabled: boolean;
  children: ReactNode;
}

export function AgentBridgeProvider({ enabled, children }: ProviderProps) {
  const [status, setStatus] = useState<WsStatus>("idle");
  const [models, setModels] = useState<string[]>([]);
  const [selectedModel, setSelectedModelState] = useState<string>(loadInitialSelectedModel);
  const wsRef = useRef<WebSocket | null>(null);
  const subscribersRef = useRef(new Set<(msg: ServerMessage) => void>());
  // Read inside `send` without recreating the callback when the user picks a new model.
  const selectedModelRef = useRef(selectedModel);
  useEffect(() => {
    selectedModelRef.current = selectedModel;
  }, [selectedModel]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let backoffMs = 500;
    let timer: number | undefined;

    const connect = () => {
      if (cancelled) return;
      setStatus("connecting");
      const ws = new WebSocket(resolveUrl());
      wsRef.current = ws;
      ws.addEventListener("open", () => {
        backoffMs = 500;
        setStatus("open");
      });
      ws.addEventListener("message", (event) => {
        if (typeof event.data !== "string") return;
        try {
          const msg = JSON.parse(event.data) as ServerMessage;
          if (msg.type === "models") {
            setModels(Array.isArray(msg.models) ? msg.models : []);
          }
          subscribersRef.current.forEach((s) => {
            try { s(msg); } catch { /* keep dispatching to other subscribers */ }
          });
        } catch {
          // ignore malformed frames
        }
      });
      const closeHandler = () => {
        if (cancelled) return;
        setStatus("closed");
        timer = window.setTimeout(connect, backoffMs);
        backoffMs = Math.min(backoffMs * 2, 10_000);
      };
      ws.addEventListener("close", closeHandler);
      ws.addEventListener("error", () => { /* close follows */ });
    };

    connect();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [enabled]);

  const send = useCallback((msg: ClientMessage): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    // Auto-inject the user-selected model into outgoing user messages so
    // wizards don't have to thread the picker through every send call.
    let payload: ClientMessage = msg;
    if (msg.type === "message" && msg.model === undefined && selectedModelRef.current) {
      payload = { ...msg, model: selectedModelRef.current };
    }
    ws.send(JSON.stringify(payload));
    return true;
  }, []);

  const subscribe = useCallback((handler: (msg: ServerMessage) => void) => {
    subscribersRef.current.add(handler);
    return () => { subscribersRef.current.delete(handler); };
  }, []);

  const setSelectedModel = useCallback((model: string) => {
    setSelectedModelState(model);
    try {
      if (model) window.localStorage.setItem(MODEL_STORAGE_KEY, model);
      else window.localStorage.removeItem(MODEL_STORAGE_KEY);
    } catch {
      // ignore — persistence is best-effort
    }
  }, []);

  return (
    <BridgeContext.Provider value={{ status, send, subscribe, models, selectedModel, setSelectedModel }}>
      {children}
    </BridgeContext.Provider>
  );
}

export function useAgentBridge(): BridgeValue {
  const ctx = useContext(BridgeContext);
  if (!ctx) throw new Error("useAgentBridge must be used inside <AgentBridgeProvider>");
  return ctx;
}
